import type { ApolloClient } from '@apollo/client'
import { notifyBugsnag } from '@salescore/client-base'
import type { ChangeHistory, ViewRecordFieldChange } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ViewQueryList,
  type ViewQueryNode,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { logger } from '@salescore/frontend-common'
import type { Dispatch } from 'react'

// eslint-disable-next-line sonarjs/deprecation
import { getInnerRecordNode, getNode, getRecordNodeFromRootNode } from '../nodeUtility'

//
// eslint-disable-next-line sonarjs/todo-tag
// TODO: mutationに移行したい
//
export const recordsAction = ({
  records,
  query,
  client,
  setRecords,
  addChange,
}: {
  records: ViewQueryRecordNode[]
  query: ViewQueryList | undefined
  client: ApolloClient<unknown>
  setRecords: Dispatch<ViewQueryRecordNode[]>
  addChange: (x: ChangeHistory) => void
}) => ({
  // eslint-disable-next-line sonarjs/cognitive-complexity
  removeRelation({
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: 共通化。この行の上にコメントすると謎のエラーになるので注意
    rowIndex,
    innerRowIndex,
    node,
  }: {
    rowIndex: number
    innerRowIndex: number
    node: ViewQueryNode
  }) {
    // query === undefined は kpi/kpiPivot view 経由で recordsAction が生成された場合のみ。
    // kpi/kpiPivot の column 型では本分岐 (関連削除) に到達しないため silent return で安全。
    if (node.read.join?.relation !== 'many_to_one' || query === undefined) {
      return
    }
    const visibleNodeNames = getVisibleNodeNames(query)
    const changes: ViewRecordFieldChange[] = []
    // eslint-disable-next-line sonarjs/deprecation
    const parentNode = getNode(query.tree, node.path.slice(0, -1))
    if (parentNode === undefined) {
      throw new Error(`parentNode not found`)
    }

    const { joinOn } = node.read.join
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
    if (joinOn === undefined) {
      throw new Error(`parentNodeColumnName undefined`)
    }
    if (joinOn.type === 'sql') {
      // i18n: エラー系は一旦保留
      throw new Error(`カスタムSQLによるJOINを定義している場合、このアクションは実行できません`)
    }
    if (joinOn.parentNodeColumnName === 'id') {
      throw new Error(`parentNodeColumnName must not be id`)
    }
    const parentReferenceField = query.fields.find((field) => {
      const [x] = field.meta.dependedPropertyNamesWithStreamName
      if (x === undefined) {
        return false
      }
      return x.streamName === joinOn.meta.parentNodeStreamName && x.propertyName === joinOn.meta.parentNodePropertyName
    })

    let newRecordsCopy: ViewQueryRecordNode[] = []

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
    if (records === undefined) {
      setRecords(records)
    } else {
      const newRecords = structuredClone(records)

      // ルートのレコード(行)を取得
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const rootRecordNode = newRecords[rowIndex]!
      // 親レコードを取得
      // eslint-disable-next-line sonarjs/deprecation
      const parentRecordNodes = getRecordNodeFromRootNode(rootRecordNode, node.path.slice(0, -1))
      const parentRecordNode = Array.isArray(parentRecordNodes)
        ? // eslint-disable-next-line sonarjs/deprecation
          getInnerRecordNode(parentRecordNodes, innerRowIndex)
        : parentRecordNodes
      if (parentRecordNode === undefined) {
        logger.debug(`parentRecordNode not found`)
        notifyBugsnag({ error: new Error(`parentRecordNode not found in removeRelation. ${node.path.join(',')}`) })
        setRecords(records)
      } else {
        //
        // 親要素に参照フィールドがあれば削除
        //
        if (parentReferenceField !== undefined) {
          parentRecordNode.attributes[parentReferenceField.name] = undefined
          parentRecordNode.attributes[`${parentReferenceField.name}_label`] = undefined
        }

        //
        // 子要素から指定した値を削除
        //
        // eslint-disable-next-line sonarjs/deprecation
        const childRecordNodes = getRecordNodeFromRootNode(rootRecordNode, node.path)
        const childRecordNode = Array.isArray(childRecordNodes)
          ? // eslint-disable-next-line sonarjs/deprecation
            getInnerRecordNode(childRecordNodes, innerRowIndex)
          : undefined
        const targetTableNode = parentRecordNode.children.find((x) => x.nodeName === node.name)
        if (targetTableNode !== undefined && childRecordNode !== undefined) {
          const index = targetTableNode.children.findIndex((x) => x.id === childRecordNode.id)
          if (index !== -1) {
            targetTableNode.children.splice(index, 1)
          }
        }

        //
        // 差分の更新
        //
        const parentReferenceFieldValue =
          parentReferenceField === undefined ? undefined : parentRecordNode.attributes[parentReferenceField.name]

        changes.push({
          id: parentRecordNode.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          streamName: parentNode.write!.streamName,
          // eslint-disable-next-line sonarjs/todo-tag
          fieldChanges: [], // TODO: referenceの削除によるcallbackは一旦無視
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: ここのchangesのロジックだけpropertySourceNameではなくpropertyNameになっている。後者に統一したい。
          before: {
            [joinOn.meta.parentNodePropertyName]: parentReferenceFieldValue ?? childRecordNode?.id ?? null, // どちらもundefinedなことはありえないはず
          },
          after: {
            [joinOn.meta.parentNodePropertyName]: null,
          },
        })

        const updatedRootRecordNode = assignInnerRowGeometry(rootRecordNode, visibleNodeNames)
        const updatedRecords = newRecords.toSpliced(rowIndex, 1, updatedRootRecordNode)
        newRecordsCopy = updatedRecords
        setRecords(updatedRecords)
      }
    }

    // changesは親要素の参照項目のみ
    addChange({
      datetime: new Date().toISOString(),
      // eslint-disable-next-line sonarjs/todo-tag
      rowIndices: [0], // TODO: 結局使ってないので廃止
      snapshot: JSON.stringify(records),
      changes,
      snapshotAfterChange: JSON.stringify(newRecordsCopy),
    })
  },
})
